import { Handle, NodeResizer, Position } from "reactflow"

interface ResizeParams {
  x: number
  y: number
  width: number
  height: number
}

interface GroupNodeData {
  label: string
  color: string
  body: string
  description?: string
  tooltip?: string
  icon?: string
  backgroundColor?: string
  searchQuery?: string
  width?: number
  height?: number
  isSelected?: boolean
  isEditing?: boolean
  onEditStart?: () => void
  onBodyCommit?: (body: string) => void
  onEditCancel?: () => void
  onResizeEnd?: (params: ResizeParams) => void
}

interface Props {
  data: GroupNodeData
  selected: boolean
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function highlightText(value: string, query?: string) {
  const trimmedQuery = query?.trim()
  if (!trimmedQuery) return value

  const pattern = new RegExp(`(${escapeRegExp(trimmedQuery)})`, "gi")
  return value.split(pattern).map((part, index) =>
    part.toLowerCase() === trimmedQuery.toLowerCase() ? (
      <mark key={`${part}-${index}`} className="rounded bg-yellow-200 px-0.5 text-slate-950">
        {part}
      </mark>
    ) : (
      part
    ),
  )
}

export function GroupNode({ data, selected }: Props) {
  const isSelected = selected || data.isSelected
  const hasTooltip = Boolean(data.tooltip)
  const startEditing = () => {
    data.onEditStart?.()
  }

  return (
    <div
      className="group pointer-events-none relative rounded-2xl border"
      style={{
        width: "100%",
        height: "100%",
        background: data.backgroundColor ?? `linear-gradient(180deg, ${data.color}10 0%, ${data.color}04 100%)`,
        borderColor: `${data.color}25`,
      }}
    >
      {hasTooltip && !data.isEditing && (
        <div
          className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 hidden w-80 -translate-x-1/2 rounded-lg border border-border bg-popover px-3 py-2.5 text-left text-xs leading-relaxed text-popover-foreground shadow-xl group-hover:block"
          style={{ borderTopColor: data.color, borderTopWidth: 3 }}
        >
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: data.color }}>
            Section
          </div>
          <div className="font-semibold text-foreground">{data.label}</div>
          <div className="mt-1.5 text-muted-foreground">{data.tooltip}</div>
        </div>
      )}
      <NodeResizer
        isVisible={isSelected}
        color={data.color}
        minWidth={220}
        minHeight={160}
        handleClassName="!h-2 !w-2 !pointer-events-auto"
        lineClassName="!border-border !pointer-events-auto"
        onResizeEnd={(_, params) => data.onResizeEnd?.(params)}
      />
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: data.color, border: "2px solid white", width: 14, height: 14, pointerEvents: "auto" }}
      />

      <div
        className="diagrammer-group-node-header pointer-events-auto flex cursor-pointer items-center gap-2 border-b px-4 py-2.5"
        role={data.isEditing ? undefined : "button"}
        tabIndex={data.isEditing ? undefined : 0}
        aria-label={`Edit ${data.label}`}
        style={{
          background: `${data.color}15`,
          borderColor: `${data.color}20`,
        }}
        onMouseDownCapture={(event) => {
          if (event.detail < 2) return
          event.stopPropagation()
          startEditing()
        }}
        onDoubleClick={(event) => {
          event.stopPropagation()
          startEditing()
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            event.stopPropagation()
            startEditing()
          }
        }}
      >
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{
            background: data.color,
            boxShadow: `0 0 6px ${data.color}60`,
          }}
        />
        {data.icon && <span className="text-sm">{data.icon}</span>}
        {data.isEditing ? (
          <textarea
            autoFocus
            defaultValue={data.body}
            className="nodrag nowheel pointer-events-auto min-h-16 w-full resize-y rounded border border-border bg-background px-2 py-1 text-xs font-semibold text-foreground outline-hidden focus:border-primary"
            onBlur={(event) => data.onBodyCommit?.(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault()
                data.onBodyCommit?.(event.currentTarget.value)
              }
              if (event.key === "Escape") {
                event.preventDefault()
                data.onEditCancel?.()
              }
            }}
          />
        ) : (
          <div className="text-xs font-bold uppercase tracking-[0.06em]" style={{ color: data.color }}>
            {highlightText(data.label, data.searchQuery)}
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: data.color, border: "2px solid white", width: 14, height: 14, pointerEvents: "auto" }}
      />
    </div>
  )
}
